import { Control, Controller, useFieldArray } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Button,
  IconButton,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { EditModeFormValues } from '../editModeFormType';

type EnumOptionsEditorProps = {
  control: Control<EditModeFormValues>;
  editValueIndex: number;
};

export const EnumOptionsEditor = ({
  control,
  editValueIndex,
}: EnumOptionsEditorProps) => {
  const intl = useIntl();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `uiEditValues.${editValueIndex}.enumOptions`,
  });

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {intl.formatMessage({
          id: 'features.queueSettings.editMode.editValues.options.title',
        })}
      </Typography>
      {fields.map((field, optionIndex) => (
        <Stack
          key={field.id}
          direction="row"
          spacing={1}
          sx={{ alignItems: 'flex-start' }}
        >
          <Controller
            control={control}
            name={`uiEditValues.${editValueIndex}.enumOptions.${optionIndex}.label`}
            render={({ field: optionField, fieldState }) => (
              <TextField
                {...optionField}
                size="small"
                label={intl.formatMessage({
                  id: 'features.queueSettings.editMode.editValues.option.label',
                })}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ flex: 1 }}
              />
            )}
          />
          <Controller
            control={control}
            name={`uiEditValues.${editValueIndex}.enumOptions.${optionIndex}.value`}
            render={({ field: optionField, fieldState }) => (
              <TextField
                {...optionField}
                size="small"
                label={intl.formatMessage({
                  id: 'features.queueSettings.editMode.editValues.option.value',
                })}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ flex: 1 }}
              />
            )}
          />
          <IconButton
            onClick={() => remove(optionIndex)}
            data-cy="edit-mode-remove-enum-option"
            sx={{ mt: 0.5 }}
          >
            <SvgIcon fontSize="small">
              <IconTrash />
            </SvgIcon>
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row">
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => append({ label: '', value: '' })}
          data-cy="edit-mode-add-enum-option"
        >
          {intl.formatMessage({
            id: 'features.queueSettings.editMode.editValues.options.add',
          })}
        </Button>
      </Stack>
    </Stack>
  );
};
